import Hls, { HlsConfig } from 'hls.js'

export const defaultHlsConfig: Partial<HlsConfig> = {
  // Enable worker for better performance
  enableWorker: true,

  // Start level (auto quality selection)
  startLevel: -1,

  // Debug mode (disable in production)
  debug: false,
}

/**
 * Low latency configuration for live streaming
 * Only essential settings for reducing latency
 */
export const lowLatencyConfig: Partial<HlsConfig> = {
  ...defaultHlsConfig,
  lowLatencyMode: true,
  backBufferLength: 0,
}

export function createHlsConfig(
  customConfig?: Partial<HlsConfig>,
  preset?: 'default' | 'lowLatency',
): Partial<HlsConfig> {
  const baseConfig = preset === 'lowLatency' ? lowLatencyConfig : defaultHlsConfig

  return {
    ...baseConfig,
    ...customConfig,
  }
}

export function isHlsSupported(): boolean {
  return Hls.isSupported()
}

export function isNativeHlsSupported(video?: HTMLVideoElement): boolean {
  const videoElement = video || document.createElement('video')
  return Boolean(
    videoElement.canPlayType('application/vnd.apple.mpegurl') ||
      videoElement.canPlayType('audio/mpegurl'),
  )
}

export function getHlsSupportStatus(video?: HTMLVideoElement): {
  isSupported: boolean
  isNative: boolean
  requiresHlsJs: boolean
  unsupported: boolean
} {
  const hlsJsSupported = isHlsSupported()
  const nativeSupported = isNativeHlsSupported(video)

  return {
    isSupported: hlsJsSupported || nativeSupported,
    isNative: nativeSupported,
    requiresHlsJs: hlsJsSupported && !nativeSupported,
    unsupported: !hlsJsSupported && !nativeSupported,
  }
}

/**
 * Parse HLS error for user-friendly message
 */
export function parseHlsError(error: any): {
  message: string
  isRecoverable: boolean
  severity: 'low' | 'medium' | 'high' | 'fatal'
} {
  const errorType = error?.type
  const errorDetails = error?.details

  // Network errors
  if (errorType === Hls.ErrorTypes.NETWORK_ERROR) {
    switch (errorDetails) {
      case Hls.ErrorDetails.MANIFEST_LOAD_ERROR:
      case Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT:
        return {
          message: 'Failed to load video stream. Please check your connection.',
          isRecoverable: true,
          severity: 'high',
        }
      case Hls.ErrorDetails.FRAG_LOAD_ERROR:
      case Hls.ErrorDetails.FRAG_LOAD_TIMEOUT:
        return {
          message: 'Video buffering error. Retrying...',
          isRecoverable: true,
          severity: 'low',
        }
      default:
        return {
          message: 'Network error occurred. Please check your connection.',
          isRecoverable: true,
          severity: 'medium',
        }
    }
  }

  // Media errors
  if (errorType === Hls.ErrorTypes.MEDIA_ERROR) {
    return {
      message: 'Media playback error.',
      isRecoverable: true,
      severity: 'medium',
    }
  }

  // Fatal errors
  if (error?.fatal) {
    return {
      message: 'Fatal error occurred. Please reload the video.',
      isRecoverable: false,
      severity: 'fatal',
    }
  }

  return {
    message: 'An unexpected error occurred.',
    isRecoverable: true,
    severity: 'medium',
  }
}
